import { useState, useEffect } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const NewsletterWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | subscribed | error
  const [message, setMessage] = useState('');

  // Check existing subscription on mount
  useEffect(() => {
    const checkStatus = async () => {
      const checkEmail = user?.email || '';
      if (!checkEmail) return;
      try {
        const res = await axios.get(`${API}/newsletter/status?email=${encodeURIComponent(checkEmail)}`);
        if (res.data.subscribed) {
          setStatus('subscribed');
          setEmail(checkEmail);
        }
      } catch { /* not subscribed */ }
    };
    checkStatus();
  }, [user?.email]);

  // Hide on music review page
  if (location.pathname === '/music-review') return null;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const subEmail = email || user?.email;
    if (!subEmail) return;
    
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email: subEmail });
      setStatus('subscribed');
      setMessage(res.data.message);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Something went wrong';
      if (detail.includes('already subscribed')) {
        setStatus('subscribed');
      } else {
        setStatus('error');
        setMessage(detail);
        setTimeout(() => setStatus('idle'), 3000);
      }
    }
  };

  if (status === 'subscribed') {
    return (
      <div className="px-3 py-2 border-t border-[#1a1a1a]" data-testid="newsletter-widget-subscribed">
        <div className="flex items-center gap-1.5 text-[#00ff88]">
          <Check size={12} />
          <span className="font-mono text-[10px] uppercase">Weekly newsletter active</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 border-t border-[#1a1a1a]" data-testid="newsletter-widget">
      <p className="text-[#888] font-mono text-[10px] uppercase mb-1.5 tracking-wider">Weekly Newsletter</p>
      <p className="text-[#555] text-[10px] mb-2 leading-tight">
        Stats, leaderboard, performance updates
      </p>
      <form onSubmit={handleSubscribe} className="flex gap-1">
        <input
          type="email"
          value={email || (user?.email || '')}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          required
          className="flex-1 min-w-0 px-2 py-1 bg-[#111] border border-[#222] text-white text-[10px] font-mono placeholder:text-[#444] focus:border-[#00CCFF] focus:outline-none"
          data-testid="newsletter-email-input"
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className="px-2 py-1 bg-[#00CCFF] text-black text-[10px] font-mono font-bold uppercase hover:bg-[#00b8e6] transition-colors disabled:opacity-50 shrink-0"
          data-testid="newsletter-subscribe-btn"
        >
          {status === 'loading' ? <Loader2 size={10} className="animate-spin" /> : <Mail size={10} />}
        </button>
      </form>
      {status === 'error' && message && (
        <p className="text-red-400 text-[9px] mt-1 font-mono">{message}</p>
      )}
    </div>
  );
};
